import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {from, Observable, switchMap} from 'rxjs';
import {environment} from '../../environments/environment';

export interface AttributeOptionView {
  key: string;
  label: string;
  tooltip?: string;
  sortOrder?: number;
}

export interface AttributeDefinitionView {
  attributeId: string;
  categoryPath: string;
  code: string;
  label: string;
  dataType: 'STRING' | 'INTEGER' | 'DECIMAL' | 'BOOLEAN' | 'ENUM' | 'MULTI_ENUM' | 'DATE' | 'RANGE';
  unit?: string;
  required: boolean;
  filterable: boolean;
  searchable: boolean;
  displayOrder: number;
  options: AttributeOptionView[];
  validationJson?: string;
}

export interface AttributeSchemaView {
  categoryPath: string;
  attributes: AttributeDefinitionView[];
}

export interface ListingAttributeValueView {
  code: string;
  label: string;
  dataType: string;
  unit?: string;
  value: unknown;
  displayValue?: string;
  /** When false, hide on public listing surfaces (supplier SKU, etc.). */
  buyerVisible?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AttributeSchemaService {
  private readonly baseUrlPromise: Promise<string>;

  constructor(private http: HttpClient) {
    this.baseUrlPromise = environment.then((env: any) => env.backendURL + '/api');
  }

  getSchema(categoryPath: string): Observable<AttributeSchemaView> {
    const params = new HttpParams().set('path', categoryPath || '*');
    return from(this.baseUrlPromise).pipe(
      switchMap(baseUrl => this.http.get<AttributeSchemaView>(`${baseUrl}/attribute-schema`, {params}))
    );
  }

  getListingAttributes(itemId: string): Observable<ListingAttributeValueView[]> {
    return from(this.baseUrlPromise).pipe(
      switchMap(baseUrl => this.http.get<ListingAttributeValueView[]>(`${baseUrl}/listings/${itemId}/attributes`))
    );
  }

  /** Build a taxonomy path from add/update listing URL segments (category-agnostic). */
  categoryPathFromUrlSegments(segments: string[]): string {
    return this.categoryPathFromFilterSegments(segments);
  }

  /** Build a taxonomy path from browse/search filter state. */
  categoryPathFromListingFilter(listingFilter: Record<string, unknown>): string {
    const keys = [
      'category',
      'subcategory',
      'subSubcategory',
      'subSubSubcategory',
      'subSubSubSubcategory',
      'subSubSubSubSubcategory',
      'subSubSubSubSubSubcategory',
      'subSubSubSubSubSubSubcategory',
      'subSubSubSubSubSubSubSubcategory',
    ];
    const parts: string[] = [];
    for (const key of keys) {
      const raw = listingFilter?.[key];
      if (!raw || `${raw}`.toLowerCase() === 'all') {
        break;
      }
      const segment = `${raw}`.trim().toLowerCase();
      const normalized = segment.startsWith('all-') ? segment.slice(4) : segment;
      if (!normalized) {
        break;
      }
      parts.push(normalized);
    }
    return parts.length ? parts.join('/') : '*';
  }

  private categoryPathFromFilterSegments(segments: string[]): string {
    const skip = new Set(['add-listing', 'add-item', 'update-item', 'home', 'item', 'items', 'all']);
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const parts: string[] = [];
    for (const segment of segments || []) {
      if (!segment || skip.has(segment.toLowerCase()) || uuidPattern.test(segment)) {
        continue;
      }
      const lower = segment.toLowerCase();
      if (lower.startsWith('all-')) {
        continue;
      }
      if (lower === 'all') {
        break;
      }
      parts.push(lower);
    }
    return parts.length ? parts.join('/') : '*';
  }

  attributesToFormValues(attributes: ListingAttributeValueView[]): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    for (const attribute of attributes || []) {
      if (attribute?.code) {
        values[attribute.code] = attribute.value;
      }
    }
    return values;
  }
}
